<!-- app-subdomain-creators\src\Components\CreatorChatSearchShowAllVideos.vue -->

<template>
  <div>
    <!-- Loop through the grouped videos -->
    <div
      v-for="(videosForMonth, month) in groupedVideos"
      :key="month"
      class="mb-8"
    >
      <div class="flex justify-between items-center mb-5">
        <h4 class="text-zinc-500 text-sm font-semibold">{{ month }}</h4>

        <!-- back to videos overview -->
        <div @click="toggleBackToVideos" class="flex gap-1 cursor-pointer">
          <ArrowLeftIcon class="w-4 text-[#2D8CFF]" />
          <div class="text-[#2D8CFF] text-sm font-semibold">
            Back to all Files
          </div>
        </div>
      </div>

      <div class="w-full space-y-4">
        <div
          v-for="(item, index) in videosForMonth"
          :key="`${month}-${item.messageId || 'v'}-${item.attachmentIndex || index}`"
          class="flex gap-2"
        >
          <CreatorChatInlineVideoPlayer
            v-if="item.fileId"
            :file-id="item.fileId"
            :file-name="item.name"
            :src="item.url || ''"
          />

          <!-- Date + avatar column -->
    <div class="flex flex-col gap-1 mt-2">
      <div class="text-xs text-zinc-400">
        {{ formatCreatedAt(item.createdAt) }}
      </div>
      <img
        :src="avatarForAttachmentItem(item)"
        alt="Sender avatar"
        class="w-6 h-6 rounded-full"
      />
    </div>
        </div>
      </div>

      
    </div>

    <!-- Load more videos (paging) -->
    <div v-if="hasMore" class="mt-4 flex justify-center">
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-200 disabled:opacity-50"
        :disabled="isLoadingMore"
        @click="onLoadMoreClicked"
      >
        <span v-if="!isLoadingMore">Load older videos</span>
        <span v-else>Loading…</span>
      </button>
    </div>

    <!-- global empty state (optional) -->
    <div
      v-if="!hasMore && (!searchVideos || searchVideos.length === 0)"
      class="mx-auto text-center py-8"
    >
      <h4 class="text-zinc-500 text-lg font-base">
        No videos have been uploaded to chat yet.
      </h4>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
import CreatorChatInlineVideoPlayer from '@/Components/CreatorChatInlineVideoPlayer.vue';
const defaultAvatarSrc = require('@/assets/media/images/default-avatar.png');


const props = defineProps({
  searchVideos: {
    type: Array,
    default: () => [],
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
  creatorAvatarSrc: {
    type: String,
    default: '',
  },
  memberAvatarSrc: {
    type: String,
    default: '',
  },
});


const emits = defineEmits(['toggleShowAllVideos', 'loadMoreVideos']);

const toggleBackToVideos = () => {
  emits('toggleShowAllVideos');
};

const onLoadMoreClicked = () => {
  emits('loadMoreVideos');
};

// Group by month (based on createdAt)
const groupedVideos = computed(() => {
  const byMonth = {};
  (props.searchVideos || []).forEach((item) => {
    const raw = item.createdAt || item.date;
    if (!raw) return;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return;

    const monthYear = d.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });

    if (!byMonth[monthYear]) {
      byMonth[monthYear] = [];
    }
    byMonth[monthYear].push(item);
  });
  return byMonth;
});

const formatCreatedAt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const avatarForAttachmentItem = (item) => {
  if (!item) {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  const senderType = item.senderType || item.sender_type || null;

  if (senderType === 'member') {
    return props.memberAvatarSrc || defaultAvatarSrc;
  }

  if (senderType === 'creator') {
    return props.creatorAvatarSrc || defaultAvatarSrc;
  }

  return props.creatorAvatarSrc || defaultAvatarSrc;
};

</script>
